import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { baseURL } from '../config';
import PageHeader from './reusable/PageHeader';
import { useTranslation } from 'react-i18next';

const ArrivedCustomersPage = () => {
  const { t } = useTranslation();
  
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(false);
  const [morningCustomers, setMorningCustomers] = useState([]);
  const [eveningCustomers, setEveningCustomers] = useState([]);
  
  const [timePeriod, setTimePeriod] = useState('morning'); 
  const [dataLoadedDate, setDataLoadedDate] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseURL}/arrived-customers?date=${selectedDate}`);
      if (response.status === 200) {
        const data = response.data;
        setMorningCustomers(data.morning || []);
        setEveningCustomers(data.evening || []);
        setDataLoadedDate(selectedDate);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const customersToDisplay = timePeriod === "morning" ? morningCustomers : eveningCustomers;
  const hasCustomers = customersToDisplay.length > 0;
  const isDataCurrent = dataLoadedDate === selectedDate;

  return (
    <div className="min-h-screen w-full bg-paper pb-20 font-sans text-primary">
      <main className="px-4 pt-4 max-w-lg mx-auto">
        <PageHeader title={t('arrived.title')} />

        {/* --- COMPACT Search & Stats Controls --- */}
        <div className="bg-white border border-line rounded-[14px] p-3.5 mb-5 shadow-sm">
          {/* Top Row: Date & Shift side-by-side */}
          <div className="flex gap-3 mb-3">
            {/* Select Date */}
            <div className="flex-1">
              <label className="block text-[9.5px] font-bold uppercase tracking-widest text-primary mb-1 ml-0.5">
                {t('arrived.labels.date')}
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="w-full px-3 py-2 bg-paper/50 border border-line rounded-lg text-[12.5px] font-medium text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
            </div>

            {/* Time Period Toggle */}
            <div className="flex-1">
              <label className="block text-[9.5px] font-bold uppercase tracking-widest text-primary mb-1 ml-0.5">
                {t('arrived.labels.shift')}
              </label>
              <div className="flex p-0.5 bg-paper border border-line rounded-lg">
                <button
                  onClick={() => setTimePeriod("morning")}
                  className={`flex-1 py-1.5 text-[12px] font-bold rounded-[6px] transition-all ${
                    timePeriod === "morning"
                      ? "bg-primary text-white shadow-sm"
                      : "text-[#6B7280] hover:text-primary"
                  }`}
                >
                  {t('arrived.shifts.morning')}
                </button>
                <button
                  onClick={() => setTimePeriod("evening")}
                  className={`flex-1 py-1.5 text-[12px] font-bold rounded-[6px] transition-all ${
                    timePeriod === "evening"
                      ? "bg-primary text-white shadow-sm"
                      : "text-[#6B7280] hover:text-primary"
                  }`}
                >
                  {t('arrived.shifts.evening')}
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Row: Action Button */}
          <button
            onClick={fetchCustomers}
            disabled={loading}
            className={`w-full py-2.5 rounded-lg font-slab font-bold text-white text-[14px] tracking-wide active:scale-[0.98] transition-all duration-150 ${
              loading ? "bg-primary/70 cursor-not-allowed" : "bg-primary shadow-sm"
            }`}
          >
            {loading ? t('arrived.labels.syncing') : t('arrived.labels.refresh')}
          </button>
        </div>

        {/* --- Quick Summary Bar --- */}
        <div className="flex items-center justify-between mb-2 ml-0.5 pr-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary">
            {customersToDisplay.length} {t('arrived.stats.arrivals_for')} {t(`arrived.shifts.${timePeriod}`)}
          </p>
          {!isDataCurrent && (
            <span className="text-[9px] bg-debit/10 text-debit border border-debit/20 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
              {t('arrived.stats.update_alert')}
            </span>
          )}
        </div>

        {/* --- Customer List --- */}
        {loading ? (
          <div className="space-y-3 mt-3">
             {[1, 2, 3].map(i => (
               <div key={i} className="h-[120px] bg-white border border-line animate-pulse rounded-2xl"></div>
             ))}
          </div>
        ) : hasCustomers ? (
          <div className="space-y-3 mt-3">
            {customersToDisplay.map((customer, index) => (
              <div
                key={index}
                className="bg-white border border-line rounded-[14px] p-3.5 shadow-sm"
              >
                <div className="flex justify-between items-start mb-3">
                  {/* Left: Avatar & Name */}
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-paper border border-dashed border-line text-primary flex items-center justify-center font-slab font-bold text-[16px] flex-shrink-0">
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-primary font-bold text-[14px] capitalize leading-tight">
                        {customer.name}
                      </h3>
                      <p className="text-[#6B7280] text-[10.5px] font-medium mt-0.5">
                        {customer.date}
                      </p>
                    </div>
                  </div>

                  {/* Right: Shift Badge */}
                  <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest rounded-full border ${
                    timePeriod === "morning" 
                      ? "bg-amber/10 text-amber border-amber/20"
                      : "bg-primary/10 text-primary border-primary/20"
                  }`}>
                    {t(`arrived.shifts.${timePeriod}`)}
                  </span>
                </div>

                {/* Bottom: Stats Grid */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="bg-paper/50 p-2.5 rounded-lg border border-line flex flex-col justify-center">
                    <p className="text-[9px] text-[#6B7280] font-bold uppercase tracking-widest">{t('arrived.card.weight')}</p>
                    <p className="text-[16px] font-mono font-bold text-primary mt-0.5">
                      {customer.weight} <span className="text-[10px] font-sans text-[#6B7280]">{t('arrived.card.ltr')}</span>
                    </p>
                  </div>
                  <div className="bg-paper/50 p-2.5 rounded-lg border border-line flex flex-col justify-center">
                    <p className="text-[9px] text-[#6B7280] font-bold uppercase tracking-widest">{t('arrived.card.fat')} %</p>
                    <p className="text-[16px] font-mono font-bold text-primary mt-0.5">
                      {customer.fat}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* --- Empty State --- */
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-paper border border-dashed border-line p-4 rounded-full mb-3">
               <svg className="h-6 w-6 text-line" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-primary font-bold text-[16px]">{t('arrived.empty.title')}</h3>
            <p className="text-[#6B7280] text-[12px] mt-1 max-w-[200px] leading-relaxed">
              {t('arrived.empty.msg')}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ArrivedCustomersPage;
